<template>
    <main class="row content__page">
      <section class="column large-full entry format-standard">
        <div class="content__page-header">
          <h1>
            <i class="fa fa-cogs" aria-hidden="true"></i>
            {{ $t('subscription.manage_title') }}
          </h1>
        </div>
  
        <el-form 
          v-if="isValidMagicToken"
          ref="formRef"
          :model="form"
          :rules="rules"
          label-position="top"
          class="subscription-form"
        >
          <p style="text-align:left">{{ $t('subscription.manage_description') }}</p>
          <br/>
          <!-- Email -->
          <el-form-item
            prop="email"
            :validate-status="computedEmailStatus"
          >
            <template #label>
              {{ $t('form_labels.email') }}
              <span v-if="!isEmailVerificed" style="color: #e74c3c; font-size: 0.85em; margin-left: 8px;">
                ({{ $t('subscription.email_not_verified') }})
                <a href="#" @click.prevent="sendEmailVerificationLink" style="margin-left: 6px;">
                  {{ $t('subscription.re_verify') }}
                </a>
              </span>
            </template>

            <el-input v-model="form.email" :placeholder="$t('form_labels.email_placeholder')" />

            <template #error>
              <div class="el-form-item__error">
                {{ emailFieldError }}
              </div>
            </template>
          </el-form-item>

          <!-- Language -->
          <el-form-item :label="$t('form_labels.language')" prop="language">
            <el-select v-model="form.language" :placeholder="$t('form_labels.select')">
              <el-option :label="$t('language.english')" value="en" />
              <el-option :label="$t('language.french')" value="fr" />
            </el-select>
          </el-form-item>
  
          <!-- Frequency -->
          <el-form-item :label="$t('form_labels.frequency')" prop="frequency">
            <el-radio-group v-model="form.frequency">
              <el-radio label="daily">{{ $t('subscription.daily') }}</el-radio>
              <el-radio label="weekly">{{ $t('subscription.weekly') }}</el-radio>
              <el-radio label="monthly">{{ $t('subscription.monthly') }}</el-radio>
            </el-radio-group>
          </el-form-item>
  
          <!-- Category Selection (optional UI) -->
          <el-form-item :label="$t('form_labels.categories')" prop="categories">
            <el-select v-model="form.categories" multiple :placeholder="$t('form_labels.select_categories')">
              <el-option v-for="category in availableCategories" :key="category.id" :label="category.name" :value="category.id" />
            </el-select>
          </el-form-item>


          <!-- Timezone -->
          <el-form-item :label="$t('form_labels.select_timezone')" prop="timezone">
          <el-cascader
            v-model="form.timezone"
            :options="formattedTimezones"
            :props="{
              expandTrigger: 'hover',
              emitPath: false, // 👈 ensures `form.timezone` gets the selected full string
              checkStrictly: false
            }"
            filterable
            style="width: 100%;"
          />
          </el-form-item>


  
          <div class="form-actions">
            <el-button type="danger" plain @click="openUnsubscribeDialog">
              {{ $t('subscription.unsubscribe_button') }}
            </el-button>
            <el-button type="secondary" @click="validateAndSubmit">
              {{ $t('subscription.update_button') }}
            </el-button>
          </div>
        </el-form>

        <div v-else style="margin-top:100px;">
          <p>{{ $t('subscription.invalid_token_message') }}</p>
            <!-- Add a message and a button to request a new magic link -->
             <br/>
            <el-button type="secondary" @click="openMagicLinkModal">
              {{ $t('subscription.request_new_magic_link') }}
            </el-button>
        </div>
      </section>
    </main>

    <!-- Unsubscibe Confirmation Dialog -->
    <ElDialog
      v-model="unsubscribeDialogVisible"
      :title="$t('subscription.confirm_unsubscribe_title')"
      width="30%"
      :before-close="handleUnsubscribeDialogClose"
    >
      <span>{{ t('dialogs.unsubscribe.confirmText') }}</span>

      <template #footer>
        <ElButton @click="unsubscribeDialogVisible = false">
          {{ t('buttons.cancel') }}
        </ElButton>
        <ElButton type="danger" @click="unsubscribe">
          {{ t('buttons.confirm') }}
        </ElButton>
      </template>
    </ElDialog>

    <SubscriptionMagicLinkModal ref="subscriptionMagicLinkModal" />

  </template>
  
  <script>
  import { ref, reactive, onMounted, watch, nextTick, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useRoute } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { ElNotification } from 'element-plus';
  import apolloClient from '@/services/apollo-client';
  import {
    GET_VISIBLE_CATEGORIES
  } from '@/graphql/categories';  
  import {
    GET_NEWSLETTER_SUBSCRIPTION_BY_TOKEN,
    UPDATE_NEWSLETTER_SUBSCRIPTION,
    DELETE_NEWSLETTER_SUBSCRIPTION,
    REQUEST_NEWSLETTER_SUBSCRIPTION_VERIFICATION_EMAIL
  } from '@/graphql/newsletter-subscriptions';
  
  import { TIMEZONES } from '@/graphql/timezones';

  import SubscriptionMagicLinkModal from '@/components/Subscriptions/SubscriptionMagicLinkModal.vue';


  export default {
    components: { SubscriptionMagicLinkModal },
    name: 'SubscriptionManagePage',
    setup() {
      const route = useRoute();
      const { t } = useI18n();
      const formRef = ref(null);
      const magicToken = ref(route.params.magic_token || '');
      const availableCategories = ref([]);
      const isValidMagicToken = ref(false);
      const unsubscribeDialogVisible = ref(false);
      const subscriptionMagicLinkModal = ref(null);
      const previousLanguage = ref('en');
      const isEmailVerificed = ref( false);
      const fieldErrors = reactive({ email: '' });
      const availableTimezones = ref([]);

      const form = reactive({
        email: '',
        language: 'en',
        frequency: 'weekly',
        categories: [],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' // default
      });

      const selectedTimezone = ref(form.timezone);



      const fetchTimezones = async () => {
        try {
          const { data } = await apolloClient.query({ query: TIMEZONES });
          availableTimezones.value = data.timezones || [];
        } catch (err) {
          console.error('Failed to fetch timezones:', err);
        }
      };

      const router = useRouter();

      // This comes from Element Plus form instance
      const emailFieldRef = computed(() =>
        formRef.value?.fields?.find((f) => f.prop === 'email')
      );

      // Combine internal + external errors
      const emailFieldError = computed(() =>
        fieldErrors.email || emailFieldRef.value?.validateMessage || ''
      );

      // Ensure the red border shows if any error exists
      const computedEmailStatus = computed(() =>
        emailFieldError.value ? 'error' : ''
      );
              
      const rules = reactive({
        email: [
          { required: true, message: t('form_rules.email_required'), trigger: 'blur' },
          { type: 'email', message: t('form_rules.email_invalid'), trigger: ['blur', 'change'] },
          {
            validator: (_, __, callback) => {
              if (fieldErrors.email) {
                callback(new Error(fieldErrors.email));
              } else {
                callback();
              }
            },
            trigger: 'blur',
            message: '' // optional fallback
          }
        ],
        language: [
          { required: true, message: t('form_rules.language_required'), trigger: 'change' }
        ],
        frequency: [
          { required: true, message: t('form_rules.frequency_required'), trigger: 'change' }
        ],
        categories: [
          { required: true, message: t('form_rules.categories_required'), trigger: 'change' }
        ],
        timezone: [
          { required: true, message: t('form_rules.timezone_required'), trigger: 'change' }
        ]
      });

      const changeTimezone = (val) => {
        form.timezone = val;
      };

      const formattedTimezones = computed(() => {
        return availableTimezones.value.reduce((acc, tz) => {
          const [region, ...rest] = tz.value.split('/');
          if (!region || rest.length === 0) return acc;

          const city = rest.join('_');
          const regionKey = region.toLowerCase().replaceAll('-', '_');
          const cityKey = `${region.toLowerCase()}_${city.toLowerCase()}`.replaceAll('-', '_');

          // Find or create the region group
          let regionGroup = acc.find(r => r.value === region);
          if (!regionGroup) {
            regionGroup = {
              value: region,
              label: t('timezones.regions.' + regionKey),
              children: [],
            };
            acc.push(regionGroup);
          }

          regionGroup.children.push({
            value: tz.value,
            label: t('timezones.cities.' + cityKey) || city.replaceAll('_', ' '),
          });

          return acc;
        }, []).sort((a, b) => a.label.localeCompare(b.label));
      });


      const sendEmailVerificationLink = async () => {
        if (!form.email) {
          ElNotification({
            title: t('notifications.error.title'),
            message: t('form_rules.email_required'),
            type: 'error',
          });
          return;
        }

        try {
          console.log("Sending verification email to:", form.email);
          const { data } = await apolloClient.mutate({
            mutation: REQUEST_NEWSLETTER_SUBSCRIPTION_VERIFICATION_EMAIL,
            variables: { email: form.email },
          });

          const response = data?.requestNewsletterSubscriptionEmailVerification;

          if (!response) throw new Error('Empty response from server.');

          switch (response.reason) {
            case 'USER_EMAIL_ALREADY_VERIFIED':
            isEmailVerificed.value = true; // ✅ Hide red label and link
              ElNotification({
                title: t('notifications.info.title'),
                message: t('notifications.subscriptions.already_verified'),
                type: 'info',
              });
              break;
            case 'USER_EMAIL_ALREADY_EXISTS':
           
            ElNotification({
                title: t('notifications.error.title'),
                message: t('form_rules.email_already_exists'),
                type: 'error',
              });


            fieldErrors.email = t('form_rules.email_already_exists');
            await nextTick(); // ensures the DOM updates
            await formRef.value.validateField('email'); // Re-triggers the validator
              break;

            case 'USER_EMAIL_VERIFICATION_SENT':
              ElNotification({
                title: t('notifications.success.title'),
                message: t('notifications.subscriptions.verification_sent'),
                type: 'info',
              });
              break;

            default:
              ElNotification({
                title: t('notifications.success.title'),
                message: response.message || t('notifications.subscriptions.verification_sent'),
                type: 'info',
              });
              break;
          }

        } catch (err) {
          ElNotification({
            title: t('notifications.error.title'),
            message: err.message,
            type: 'error',
          });
        }
      };
  
      const fetchData = async () => {
        try {
          const { data: subData } = await apolloClient.query({
            query: GET_NEWSLETTER_SUBSCRIPTION_BY_TOKEN,
            variables: { magic_token: magicToken.value }
          });
          
          // Check if subscription data is valid if so set isValidMagicToken to true
          isValidMagicToken.value = !!subData.newsletterSubscriptionByToken;

          isEmailVerificed.value = subData.newsletterSubscriptionByToken?.user?.email_verified_at != null ?? false;
        
          if (!subData.newsletterSubscriptionByToken) {
            ElNotification({
              title: t('notifications.error.title'),
              message: t('notifications.subscriptions.invalid_token'),
              type: 'error'
            });
            return;
          }
  
          const { data: categoryData } = await apolloClient.query({ query: GET_VISIBLE_CATEGORIES });
          availableCategories.value = categoryData.visibleCategories;
  
          const sub = subData.newsletterSubscriptionByToken;
          Object.assign(form, {
            email: sub.user.email,
            language: sub.user.language ?? 'en',
            frequency: sub.frequency ?? 'weekly',
            categories: sub.categories?.map(c => c.id) ?? [],
            timezone: sub.user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' 
          });

          previousLanguage.value = sub.language ?? 'en';

        } catch (err) {
          ElNotification({
            title: t('notifications.error.title'),
            message: t('notifications.error.message'),
            type: 'error'
          });
        }
      };

      const emitChangeLocale = (locale) => {
        const url = new URL(window.location.href);
        url.searchParams.set('lang', locale);
        window.location.href = url.toString();
      };
        
      const validateAndSubmit = () => {
        formRef.value.validate(async (valid) => {
          if (!valid) return;

          previousLanguage.value = form.language;

          try {
            const { data } = await apolloClient.mutate({
              mutation: UPDATE_NEWSLETTER_SUBSCRIPTION,
              variables: {
                input: {
                  magic_token: magicToken.value,
                  email: form.email,
                  language: form.language,
                  frequency: form.frequency,
                  categories: form.categories,
                  timezone: form.timezone
                }
              }
            });

            const response = data?.updateNewsletterSubscription;
  
            if (data?.updateNewsletterSubscription?.success) {
              ElNotification({
                title: t('notifications.success.title'),
                message: t('notifications.subscriptions.updated'),
                type: 'success'
              });

              if (form.language !== previousLanguage.value) {
                setTimeout(() => {
                  emitChangeLocale(form.language);
                }, 2000); // 1s delay after success
              }

            } else {

              const message = t('notifications.subscriptions.email_exists');

              switch (response.reason) {
                case 'USER_EMAIL_ALREADY_EXISTS':

                fieldErrors.email = message;

                ElNotification({
                    title: t('notifications.error.title'),
                    message: message,
                    type: 'error',
                  });

                await nextTick(); // ensures the DOM updates
                await formRef.value.validateField('email'); // Re-triggers the validator
               
  
                  break;

                default:
                  ElNotification({
                    title: t('notifications.error.title'),
                    message: response.message || 'Update failed',
                    type: 'error',
                  });
                  break;
              }
            }
          } catch (err) {
            console.log('Error during update:', err);
          }
        });
      };

      const unsubscribe = async () => {
        try {
          const { data } = await apolloClient.mutate({
            mutation: DELETE_NEWSLETTER_SUBSCRIPTION,
            variables: {
              magic_token: magicToken.value,
            },
          });

          if (data?.deleteNewsletterSubscription?.success) {
            ElNotification({
              title: t('notifications.success.title'),
              message: t('notifications.subscriptions.unsubscribed'),
              type: 'success',
            });

            // Wait a moment for the user to see the notification, then redirect
            setTimeout(() => {
              router.push({ name: 'HomePage' });
            }, 1500); // 1.5 second delay

          } else {
            throw new Error(data.deleteNewsletterSubscription.message || 'Unsubscribe failed');
          }
        } catch (err) {
          ElNotification({
            title: t('notifications.error.title'),
            message: t('notifications.subscriptions.error'),
            type: 'error',
          });
        } finally {
          unsubscribeDialogVisible.value = false;
        }
      };

      const openUnsubscribeDialog = () => {
        unsubscribeDialogVisible.value = true;
      };

      const handleUnsubscribeDialogClose = (done) => {
        unsubscribeDialogVisible.value = false;
        done();
      };


      const openMagicLinkModal = () => {
        subscriptionMagicLinkModal.value?.open?.();
      };

      onMounted(() => {
        fetchData(); 
        fetchTimezones();
      });

      watch(() => form.email, () => {
        if (fieldErrors.email) {
          fieldErrors.email = '';
          nextTick(() => {
            formRef.value?.validateField('email'); // re-run validation
          });
        }
      });

  
      return {
        t,
        form,
        rules,
        isValidMagicToken,
        formRef,
        availableCategories,
        validateAndSubmit,
        isEmailVerificed,
        openUnsubscribeDialog,
        unsubscribe,
        unsubscribeDialogVisible,
        handleUnsubscribeDialogClose,
        subscriptionMagicLinkModal,
        sendEmailVerificationLink,
        openMagicLinkModal,
        fieldErrors,
        emailFieldError,
        computedEmailStatus,
        emailFieldRef,
        availableTimezones,
        selectedTimezone,
        formattedTimezones,
        changeTimezone,
      };
    }
  };
  </script>
  
  <style scoped>
  .subscription-form {
    margin-top: 2rem;
    max-width: 600px;
    margin: 2rem auto;
  }
  .form-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
  }
  .el-cascader {
    width: 100%;
  }

  </style>
  